import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  History as AuditTrailIcon,
  OpenInNew as OpenInNewIcon,
  SmartToy as CopilotIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import type { Incident } from '../../services/incidentsApi';

interface IncidentDrawerSupportPanelProps {
  drawerCardSx: any;
  cardHeadingSx: any;
  selectedIncident: Incident;
  derivePolicyNotificationRoutes: (metadata: any) => any[];
  PolicyNotificationStatusChip: React.ComponentType<any>;
  notificationEditorError: string | null;
  policyScopeForEditing: {
    policyId?: string | null;
    siteId?: string | null;
  };
  canUseOpsAutomation: boolean;
  setNotificationEditorOpen: (open: boolean) => void;
  handleOpenPolicyEditor: () => void;
  detectionRuleSummary?: string | null;
  handleSyncFromOdoo: (incidentId: string) => void;
  canOpenAuditTrail: boolean;
  openAuditTrail: () => void;
  canOpenCopilot: boolean;
  navigate: (path: string) => void;
  buildContextBundle: (value: any) => string;
  activeScope: any;
  scopedSiteId: string | null | undefined;
  opsStateResult: any;
  user: any;
  notificationEditorOpen: boolean;
  emailTargetDraft: string;
  setEmailTargetDraft: (value: string) => void;
  smsTargetDraft: string;
  setSmsTargetDraft: (value: string) => void;
  webhookTargetDraft: string;
  setWebhookTargetDraft: (value: string) => void;
  notificationEditorDeployNow: boolean;
  setNotificationEditorDeployNow: (value: boolean) => void;
  notificationEditorBusy: boolean;
  handleSaveNotificationTargets: () => Promise<void> | void;
}

const IncidentDrawerSupportPanel: React.FC<IncidentDrawerSupportPanelProps> = ({
  drawerCardSx,
  cardHeadingSx,
  selectedIncident,
  derivePolicyNotificationRoutes,
  PolicyNotificationStatusChip,
  notificationEditorError,
  policyScopeForEditing,
  canUseOpsAutomation,
  setNotificationEditorOpen,
  handleOpenPolicyEditor,
  detectionRuleSummary,
  handleSyncFromOdoo,
  canOpenAuditTrail,
  openAuditTrail,
  canOpenCopilot,
  navigate,
  buildContextBundle,
  activeScope,
  scopedSiteId,
  opsStateResult,
  user,
  notificationEditorOpen,
  emailTargetDraft,
  setEmailTargetDraft,
  smsTargetDraft,
  setSmsTargetDraft,
  webhookTargetDraft,
  setWebhookTargetDraft,
  notificationEditorDeployNow,
  setNotificationEditorDeployNow,
  notificationEditorBusy,
  handleSaveNotificationTargets,
}) => {
  const routes = selectedIncident.policy_notification_routes
    ?? derivePolicyNotificationRoutes(selectedIncident.envelope_metadata);

  const handleCopySupportContext = React.useCallback(() => {
    try {
      const value = buildContextBundle({
        orgId: activeScope?.orgId,
        siteId: scopedSiteId,
        page: 'incidents',
        state: opsStateResult.state,
        reasons: opsStateResult.reasons,
        userRole: user?.role,
        deviceId: selectedIncident?.device_id || undefined,
        cameraId: selectedIncident?.camera_id || undefined,
        extra: {
          incident_id: selectedIncident.incident_id,
          severity: (selectedIncident as any)?.severity,
          status: (selectedIncident as any)?.status,
          ops_state: (selectedIncident as any)?.ops_state,
        },
      });
      navigator.clipboard?.writeText(value).catch(() => {});
    } catch {
      // ignore clipboard errors in the review surface
    }
  }, [activeScope, buildContextBundle, opsStateResult, scopedSiteId, selectedIncident, user]);

  return (
    <>
      <Paper variant="outlined" sx={drawerCardSx}>
        <Typography sx={{ ...cardHeadingSx, mb: 0.55 }}>Notification channels & downstream</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.35, lineHeight: 1.55 }}>
          Delivery and handoff state for the notification channels tied to this detection rule.
        </Typography>
        <Grid container spacing={1.25}>
          {routes.map((route: any) => {
            const demote = ['not_listed', 'no_snapshot', 'disabled'].includes(String(route.state));
            const routeLabel = String(route.label || '') === 'Webhook (policy)' ? 'Webhook' : String(route.label || '');
            return (
              <Grid item xs={12} sm={6} key={route.action_type}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1, borderRadius: 2, opacity: demote ? 0.72 : 1, bgcolor: demote ? 'grey.50' : 'background.paper' }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', color: 'text.secondary' }}>
                    {routeLabel}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <PolicyNotificationStatusChip route={route} />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.4 }}>
          Recipients are inherited from the detection rule. Adjust email, SMS, or webhook destinations from the rule editor when this incident needs different follow-through.
        </Typography>
        {notificationEditorError ? <Alert severity="warning" sx={{ mt: 1.2 }}>{notificationEditorError}</Alert> : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, fontWeight: 700 }}>
          Support tools
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setNotificationEditorOpen(true)}
            disabled={!policyScopeForEditing.policyId || !policyScopeForEditing.siteId || !canUseOpsAutomation}
          >
            Edit notification targets here
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={handleOpenPolicyEditor}
            disabled={!detectionRuleSummary}
          >
            Edit detection rule notifications
          </Button>
          {selectedIncident?.odoo_ticket_url && (
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(selectedIncident.odoo_ticket_url, '_blank')}
            >
              Open work order
            </Button>
          )}
          {selectedIncident?.odoo_ticket_id && canUseOpsAutomation && (
            <Button
              size="small"
              startIcon={<SyncIcon />}
              onClick={() => handleSyncFromOdoo(selectedIncident.incident_id)}
            >
              Sync from Odoo
            </Button>
          )}
          {canOpenAuditTrail && (
            <Button
              size="small"
              variant="text"
              startIcon={<AuditTrailIcon />}
              onClick={openAuditTrail}
            >
              Open audit trail
            </Button>
          )}
          {canOpenCopilot && selectedIncident?.device_id && (
            <Button
              size="small"
              variant="text"
              startIcon={<CopilotIcon />}
              onClick={() => navigate(`/ops/copilot?device=${encodeURIComponent(selectedIncident.device_id || '')}`)}
            >
              Open Copilot
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            onClick={handleCopySupportContext}
          >
            Copy support context
          </Button>
        </Box>
      </Paper>
      <Dialog open={notificationEditorOpen} onClose={() => setNotificationEditorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit detection rule notification targets</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This updates the detection rule itself, not just this single incident. Save and deploy when you want future incidents from the same rule to notify the new targets.
          </Alert>
          <TextField
            fullWidth
            size="small"
            label="Email recipients"
            placeholder="ops@example.com, supervisor@example.com"
            value={emailTargetDraft}
            onChange={(event) => setEmailTargetDraft(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="SMS recipients"
            placeholder="+15551234567"
            value={smsTargetDraft}
            onChange={(event) => setSmsTargetDraft(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Webhook URL"
            placeholder="https://example.com/incidents"
            value={webhookTargetDraft}
            onChange={(event) => setWebhookTargetDraft(event.target.value)}
          />
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={(
              <Switch
                checked={notificationEditorDeployNow}
                onChange={(event) => setNotificationEditorDeployNow(event.target.checked)}
              />
            )}
            label="Deploy the updated rule immediately"
          />
          {notificationEditorError ? <Alert severity="warning" sx={{ mt: 1 }}>{notificationEditorError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSaveNotificationTargets()}
            disabled={notificationEditorBusy || !policyScopeForEditing.policyId || !policyScopeForEditing.siteId}
          >
            {notificationEditorBusy ? 'Saving…' : 'Save notification targets'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncidentDrawerSupportPanel;
